import { useState } from 'react';
import { Button, Card, Col, Empty, Modal, Row, Skeleton, Space, Tooltip, message } from 'antd';
import { FileExcelOutlined, FileWordOutlined, PrinterOutlined } from '@ant-design/icons';
import { Permissions } from '@tcr-erp/shared';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { useOrderDetail } from '@/hooks/useOrderDetail';
import { useGoodsIssueDetail, useStockTransferDetail } from '@/hooks/useInventoryDocs';
import { OrderInfoPanel } from '../../POS/components/OrderInfoPanel';
import { ShippingTotals } from '../../POS/components/Fulfillment/ShippingTotals';
import { OrderAccountingCard } from '@/components/ecommerce/orders/OrderAccountingCard';
import { BranchCard, StaffCard } from './BranchStaffCard';
import { InvoiceCard } from './InvoiceCard';
import { OrderSummaryCustomerCard } from './OrderSummaryCustomerCard';
import { OrderSummaryItems } from './OrderSummaryItems';
import { OrderSummaryPaymentCard } from './OrderSummaryPaymentCard';
import { OrderSummaryShippingExtra } from './OrderSummaryShippingExtra';
import { OrderQrPayment } from './OrderQrPayment';
import { OrderReceiptsList } from './OrderReceiptsList';
import { ShippingCard } from './ShippingCard';
import { useExportSingleOrder } from './ExportOrderButton';
import { printOrderReceipt } from '../utils/printReceipt';
import { printOrderCustomerDoc } from '../utils/printOrderCustomerDoc';
import { exportOrderCustomerDocx } from '../utils/exportOrderCustomerDoc';
import { buildWholesaleDocxFileName, isWholesaleB2BOrder } from '../utils/wholesaleOrder';
import {
  ORDER_STATUS_GROUP_COLOR,
  ORDER_STATUS_GROUP_LABEL,
  resolveOrderStateColor,
  resolveOrderStateLabel,
} from '../parts/constants';
import { canEditOrder, isOrderLockedForUser, ORDER_STATUS_LOCK_TOOLTIP } from '../utils/orderHeaderActions';
import { orderToMoneyResult } from '../utils/orderToMoneyResult';
import { buildOrderReceipts } from '../utils/orderReceipts';
import { splitPromotionLabels, sumNonFlashPromotionDiscount } from '@/utils/promotionDiscount';
import '../orders-detail.css';
import './order-summary-modal.css';
import { hasAnyRole } from '@/utils/userRoles';

export interface OrderSummaryModalProps {
  /** `null` = không mở. Cùng quy ước với `OrderSummaryModal` của ECom. */
  orderId: string | null;
  onClose: () => void;
  /** Mở trang chi tiết đầy đủ (`/orders/:id`). */
  onViewDetail: (orderId: string) => void;
  /** Mở màn sửa đơn (`/orders/:id/edit`). */
  onEditOrder: (orderId: string) => void;
}

/**
 * Pop-up xem nhanh đơn hàng khi bấm mã đơn ở danh sách đơn ngoài sàn (TCR-1095) — bố cục 3 cột
 * như salework và như pop-up bên ECom (`pages/ECom/orders/parts/OrderSummaryModal`), để hai
 * phân hệ nhìn giống nhau.
 *
 * Toàn bộ ruột dùng lại ĐÚNG các card của trang chi tiết, chỉ khác là không truyền `onEdit`:
 * mọi thao tác sửa (đổi khách, đổi chi nhánh/nhân viên, sửa giao nhận, lập phiếu thu) vẫn nằm ở
 * trang chi tiết. Dựng lại UI riêng cho pop-up sẽ đẻ ra một nguồn số/nhãn thứ hai, đúng thứ
 * ticket này đang đi sửa.
 */
export function OrderSummaryModal({ orderId, onClose, onViewDetail, onEditOrder }: OrderSummaryModalProps) {
  const { user } = useAuth();
  const { hasPermission } = useRbac();
  const { data: order, isLoading } = useOrderDetail(orderId ?? undefined);
  const stockTransferId = order?.pos_fulfillment?.documents?.stock_transfer_id;
  const posFulfillmentDocuments = order?.pos_fulfillment?.documents;
  const goodsIssueId = posFulfillmentDocuments?.goods_issue_id
    ?? posFulfillmentDocuments?.transit_goods_issue_id;
  const { data: stockTransfer } = useStockTransferDetail(stockTransferId);
  const { data: goodsIssue } = useGoodsIssueDetail(goodsIssueId);

  // TCR-2480: xuất đúng đơn đang xem trong pop-up (không gọi lại danh sách theo bộ lọc).
  const { exportingSingle, exportSingleOrder } = useExportSingleOrder(order);

  /**
   * TCR-2480: y hệt `handlePrint` ở `Orders/detail.tsx` — pop-up và trang chi tiết phải in
   * cùng một đường: `printOrderReceipt` tự quyết định in qua hardware agent hay mở hộp thoại
   * in của trình duyệt. Chỉ báo thành công khi lệnh in đi qua agent, vì chế độ browser thì
   * người dùng còn phải tự bấm "In" trong hộp thoại hệ thống.
   */
  const [printing, setPrinting] = useState(false);
  const handlePrintReceipt = async () => {
    // Chặn bấm đúp: mỗi lần bấm là một lệnh in gửi tới máy in.
    if (!order || printing) return;
    setPrinting(true);
    try {
      const res = await printOrderReceipt(
        order,
        { storeName: order.store?.name, storeAddress: order.store?.address },
        'receipt',
      );
      if (res && res.method !== 'browser') {
        message.success('Đã gửi lệnh in hoá đơn POS');
      }
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(err ?? (e as Error)?.message ?? 'In hoá đơn POS thất bại');
    } finally {
      setPrinting(false);
    }
  };

  /**
   * TCR-2506 — đơn SỈ GT/MT dùng MẪU "ĐƠN HÀNG" riêng (khung Word mẫu của
   * khách) thay cho hoá đơn nhiệt: in ra giấy A4 cho khách tổ chức, kèm nút
   * xuất file .docx để gửi kèm hồ sơ. Đơn thường giữ nguyên `printOrderReceipt`.
   */
  const isWholesale = Boolean(order) && isWholesaleB2BOrder(order);
  const [printingDoc, setPrintingDoc] = useState(false);
  const handlePrintOrderDoc = async () => {
    if (!order || printingDoc) return;
    setPrintingDoc(true);
    try {
      await printOrderCustomerDoc(order);
    } catch (e: unknown) {
      message.error((e as Error)?.message ?? 'In đơn hàng thất bại');
    } finally {
      setPrintingDoc(false);
    }
  };

  const [exportingDoc, setExportingDoc] = useState(false);
  const handleExportOrderDocx = async () => {
    if (!order || exportingDoc) return;
    setExportingDoc(true);
    try {
      await exportOrderCustomerDocx(
        order,
        buildWholesaleDocxFileName(order.order_number, new Date()),
      );
      message.success('Đã xuất file Word đơn hàng');
    } catch (e: unknown) {
      message.error((e as Error)?.message ?? 'Xuất file Word đơn hàng thất bại');
    } finally {
      setExportingDoc(false);
    }
  };

  const statusGroup = order?.status_group && order.status_group !== 'all' ? order.status_group : undefined;
  const statusLabel = (statusGroup ? ORDER_STATUS_GROUP_LABEL[statusGroup] : undefined)
    ?? (order ? resolveOrderStateLabel(order.state) : '');
  const statusColor = (statusGroup ? ORDER_STATUS_GROUP_COLOR[statusGroup] : undefined)
    ?? (order ? resolveOrderStateColor(order.state) : 'default');

  // Cùng luật với nút "Sửa đơn" ở trang chi tiết và menu ... của danh sách (TCR-1094).
  // RBAC fix (2026-09-01) — canEditOrder giờ nhận canUpdateOrder (permission), không phải role.
  const canEdit = Boolean(order) && canEditOrder(order!.state, hasPermission('orders.update'));
  // TCR-1964 — tầng khoá thứ 2 theo trạng thái, cùng luật với trang chi tiết/danh sách.
  const lockedForUser = Boolean(order) && isOrderLockedForUser(order!.status_group, hasPermission(Permissions.ORDERS_OVERRIDE_LOCK));
  // Khối kế toán chỉ dành cho người có nghiệp vụ sổ sách — giống điều kiện ở `detail.tsx`.
  // TCR-1639 (fix round 2) — modal này tính isAccountantOrOwner độc lập với detail.tsx,
  // nên fix trước đó (76e39fb2, chỉ sửa detail.tsx) không lan sang đây: accountant bấm
  // vào dòng đơn hàng ở danh sách sẽ mở modal này trước (xem Orders/index.tsx
  // handleOpenSummary/useOrderColumns onOpenSummary), vẫn không thấy chứng từ kế toán dù
  // trang chi tiết đầy đủ đã đúng — đúng pattern TCR-1300 (cùng field, nhiều nơi tính lại).
  const isAccountantOrOwner = hasAnyRole(user, ['owner', 'manager', 'accountant']);

  const items = order?.items ?? [];
  // CÙNG nguồn số với trang chi tiết, POS và màn sửa đơn: đọc thẳng số đã lưu, không tính lại.
  const money = order ? orderToMoneyResult({ ...order, summary: order.summary ?? {} }) : undefined;
  const appliedPromotions = order?.summary?.applied_promotions ?? [];
  const appliedVouchers = order?.summary?.applied_vouchers ?? [];
  // TCR-1143 (final review, findings #2/#3): loại entry `type: 'flash_sale'`
  // khỏi tổng "Khuyến mãi" hiển thị — dòng flash sale đã mang giá đã giảm
  // sẵn, cộng thêm khoản này vào đây là trừ giá flash lần thứ 2 trên số
  // hiển thị. Xem comment đầy đủ ở `src/utils/promotionDiscount.ts` và
  // `Orders/detail.tsx` (cùng bug, cùng fix, dùng chung một hàm).
  const promotionDiscount = sumNonFlashPromotionDiscount(appliedPromotions);
  const promotionLabels = splitPromotionLabels(appliedPromotions);
  // TCR-1150 Bug2a: `summary.voucher_discount` (nguồn của `money.orderDiscount`)
  // là bucket GỘP voucher + KM tự động (`orders-aggregate.service.ts` ghi
  // `voucher_discount = commit.voucher_discount + promotionOnlyDiscount`) — KHÔNG
  // phải riêng voucher. `applied_vouchers[].discount_amount` mới là số THẬT của
  // voucher, đủ để tách dòng "Giảm giá voucher" không lẫn số KM tự động.
  const voucherDiscount = appliedVouchers.reduce(
    (sum, voucher) => sum + Number(voucher.discount_amount ?? 0),
    0,
  );
  const receipts = buildOrderReceipts(order);
  const carrierReconciliationEvent = order?.pos_fulfillment?.events?.find(
    (event) => event.event_type === 'carrier_reconciled',
  );
  const carrierReconciliation = order?.pos_fulfillment?.mode === 'carrier'
    ? {
        completed: !!carrierReconciliationEvent,
        occurredAt: carrierReconciliationEvent?.occurred_at ?? null,
        statementId: typeof carrierReconciliationEvent?.evidence?.carrier_statement_id === 'string'
          ? carrierReconciliationEvent.evidence.carrier_statement_id
          : null,
      }
    : undefined;

  return (
    <Modal
      open={Boolean(orderId)}
      onCancel={onClose}
      width={1240}
      style={{ top: 24 }}
      destroyOnHidden
      // Đơn nhiều thông tin cao hơn cả màn hình. Không giới hạn chiều cao thì chân pop-up
      // (Xem chi tiết / Sửa đơn hàng) rơi xuống dưới đáy trình duyệt, phải cuộn CẢ TRANG mới
      // thấy — cho riêng thân pop-up cuộn để hai nút luôn nằm trong tầm mắt.
      //
      // `overflowX: hidden`: pop-up chỉ được cuộn DỌC. Thanh cuộn ngang ở đáy pop-up là dấu
      // hiệu một khối con tràn bề ngang, phải sửa ở khối đó (xem `OrderSummaryItems`), không
      // để người dùng phải kéo ngang mới đọc hết một cột.
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', overflowX: 'hidden' } }}
      className="order-summary-modal"
      // Tiêu đề chỉ còn mã đơn: tag trạng thái, tên khách và ngày tạo đã xuống khối "Thông tin
      // khách hàng" theo góp ý tester 13/08 — để cả hai nơi là hiện trùng.
      title={order ? `Đơn hàng ${order.order_number}` : 'Xem nhanh đơn hàng'}
      footer={
        order ? (
          /* `wrap`: trên màn hẹp 5 nút không còn vừa một hàng, để chúng xuống dòng thay vì
             tràn ra ngoài khung pop-up (TCR-2480). */
          <Space wrap>
            <Button onClick={onClose}>Đóng</Button>
            <Button type="primary" onClick={() => onViewDetail(order._id)}>
              Xem chi tiết
            </Button>
            {/* "Xem chi tiết" là hành động chính của pop-up nên giữ một mình màu primary —
                hai nút đỏ cạnh nhau đọc như hai nút xác nhận trùng nhau. */}
            {canEdit && (
              <Tooltip title={lockedForUser ? ORDER_STATUS_LOCK_TOOLTIP : undefined}>
                <span>
                  <Button disabled={lockedForUser || undefined} onClick={() => onEditOrder(order._id)}>
                    Sửa đơn hàng
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* TCR-2480: hai thao tác giấy tờ ngay trong pop-up — trước đây muốn in/xuất phải
                mở trang chi tiết hoặc quay ra danh sách. Cùng cổng quyền với trang chi tiết
                (`orders.print.receipt`) và với mục "Xuất Excel" của danh sách (`orders.export`).
                TCR-2506: đơn sỉ GT/MT in theo MẪU ĐƠN HÀNG (khung Word mẫu) + xuất .docx;
                đơn thường giữ nguyên in hoá đơn nhiệt. */}
            {hasPermission('orders.print.receipt') && (
              isWholesale ? (
                <>
                  <Button icon={<PrinterOutlined />} loading={printingDoc} onClick={handlePrintOrderDoc}>
                    In đơn hàng
                  </Button>
                  <Button icon={<FileWordOutlined />} loading={exportingDoc} onClick={handleExportOrderDocx}>
                    Xuất Word
                  </Button>
                </>
              ) : (
                <Button icon={<PrinterOutlined />} loading={printing} onClick={handlePrintReceipt}>
                  In đơn hàng
                </Button>
              )
            )}
            {hasPermission('orders.export') && (
              <Button icon={<FileExcelOutlined />} loading={exportingSingle} onClick={exportSingleOrder}>
                Xuất Excel
              </Button>
            )}
          </Space>
        ) : null
      }
    >
      {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {!isLoading && !order && <Empty description="Không tải được đơn hàng" />}
      {!isLoading && order && money && (
        // `gutter` của antd dựng khoảng cách cột bằng margin ÂM trên Row — thân pop-up không có
        // padding bù nên hàng rộng hơn khung đúng 16px, sinh cuộn ngang. Trả margin về 0:
        // khoảng cách giữa hai cột vẫn do padding của Col lo.
        // KHÔNG `align="top"`: với `flex-start` mỗi cột chỉ cao bằng nội dung của nó, đường kẻ
        // dọc giữa các cột đứt ngang chừng thay vì chạy suốt như mẫu salework. Để mặc định
        // `stretch` cho ba cột cao bằng nhau.
        <Row gutter={[16, 16]} style={{ marginLeft: 0, marginRight: 0 }}>
          <Col xs={24} md={8} className="order-summary-col" style={{ minWidth: 0 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <OrderSummaryCustomerCard order={order} statusLabel={statusLabel} statusColor={statusColor} />
              <BranchCard order={order} hideAvatar />
              <StaffCard order={order} hideAvatar />
              <InvoiceCard order={order} />
              {isAccountantOrOwner && (
                <OrderAccountingCard
                  accounting={order.accounting}
                  eInvoice={order.e_invoice}
                  posFulfillmentDocuments={posFulfillmentDocuments ? {
                    ...posFulfillmentDocuments,
                    goods_issue_code: goodsIssue?.documentNo ?? null,
                  } : null}
                  stockTransferId={stockTransferId}
                  stockTransferCode={stockTransfer?.code ?? null}
                  carrierReconciliation={carrierReconciliation}
                />
              )}
            </Space>
          </Col>

          <Col xs={24} md={8} className="order-summary-col" style={{ minWidth: 0 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card className="order-detail-card" size="small" title="Sản phẩm">
                <OrderSummaryItems items={items} />
              </Card>

              <Card className="order-detail-card" size="small" title="Tổng hợp tài chính">
                <OrderInfoPanel
                  itemCount={items.length}
                  money={money}
                  // Chỉ để HIỂN THỊ — `money` đã trừ các khoản này rồi.
                  voucherDiscount={voucherDiscount}
                  promotionDiscount={promotionDiscount}
                  // TCR-1352: khoản giảm do đổi điểm tích luỹ — `money` đã trừ sẵn, chỉ để hiển thị.
                  loyaltyRedeemValue={Number(order.summary?.loyalty_discount ?? 0)}
                  // TCR-1556: CK tích luỹ B2B — cùng quy ước "chỉ hiển thị" như dòng trên.
                  // Pop-up dùng chung `OrderInfoPanel` với trang chi tiết nên phải truyền
                  // cùng bộ prop, nếu không hai màn lại lệch số như trước.
                  tradeDiscount={Number(order.trade_discount_amount ?? 0)}
                  appliedLabels={{
                    vouchers: appliedVouchers
                      .map((voucher) => ({
                        code: voucher.voucher_code ?? voucher.code ?? '',
                        amount: Number(voucher.discount_amount ?? 0),
                      }))
                      .filter((voucher) => voucher.code !== ''),
                    // Chip freeship phải nằm dưới dòng "Miễn phí vận chuyển",
                    // không lẫn vào nhóm chiết khấu tiền hàng.
                    promotions: promotionLabels.goods,
                    shippingPromotions: promotionLabels.shipping,
                  }}
                  shippingMode={order.shipping?.shipping_mode}
                  feePayer={order.shipping?.fee_payer}
                  carrierFee={order.shipping?.shipping_cost ? Number(order.shipping.shipping_cost) : undefined}
                  prepaidAmount={Number(order.shipping?.prepaid_amount ?? 0)}
                  readOnly
                />
                {/* Đơn bán tại quầy (không `shipping`) không có khái niệm COD — hiện khối này
                    sẽ bịa ra một "Phải thu COD: 0" vô nghĩa. Cùng điều kiện `detail.tsx` dùng. */}
                {!!order.shipping?.shipping_mode && (
                  <ShippingTotals
                    cod={money.cod}
                    debt={money.debt}
                    codDefault={money.codDefault}
                    orderRevenue={money.orderRevenue}
                  />
                )}
              </Card>

              {/* Trạng thái thanh toán đứng TRÊN cụm phiếu thu (góp ý tester 13/08): người đọc
                  cần biết đơn đã thu tiền chưa rồi mới xem thu bằng những phiếu nào. */}
              <OrderSummaryPaymentCard order={order} />
              {isAccountantOrOwner && <OrderReceiptsList receipts={receipts} />}
              <OrderQrPayment order={order} />
            </Space>
          </Col>

          <Col xs={24} md={8} className="order-summary-col" style={{ minWidth: 0 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <ShippingCard order={order} hideCarrierInfo />
              <OrderSummaryShippingExtra order={order} />
              <Card className="order-detail-card" size="small" title="Ghi chú">
                <div style={{ color: order.note ? '#333' : '#999', fontStyle: order.note ? 'normal' : 'italic' }}>
                  {order.note || 'Chưa có ghi chú'}
                </div>
              </Card>
            </Space>
          </Col>
        </Row>
      )}
    </Modal>
  );
}

export default OrderSummaryModal;
